
import React, { useState, useEffect, useMemo, Suspense, useRef, ErrorInfo, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AppProvider, useApp } from './context/AppContext';
import { LogOut, ClipboardList, Settings as SettingsIcon, UtensilsCrossed, IceCream, Home, Video, Loader2, ArrowLeft, Camera, ShieldAlert, ShoppingBag, Bike, LayoutDashboard, RefreshCw, AlertCircle, CheckCircle2, Eye, Bell, BellOff, Sparkles, MonitorPlay } from 'lucide-react';
import { signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth'; 
import { query, collection, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase';
import { setGlobalProRetailMode, getTenantId, getAdminTenant, getTenantMetadata, addVisitedTenant, getVisitedTenants, resolveTenantIdFromDomain } from './services/dbService';
import { APP_VERSION } from './version';
import { useIotAutoPrint } from './hooks/useIotAutoPrint';
import { getTranslation, Language } from './utils/i18n';

// View Imports (Lazy Loaded for performance)
const LandingView = React.lazy(() => import('./components/views/LandingView').then(m => ({ default: m.LandingView })));
const KioskAttractView = React.lazy(() => import('./components/views/KioskAttractView').then(m => ({ default: m.KioskAttractView })));
const IntroView = React.lazy(() => import('./components/views/IntroView').then(m => ({ default: m.IntroView })));
const MenuSelectionView = React.lazy(() => import('./components/views/MenuSelectionView').then(m => ({ default: m.MenuSelectionView })));
const BuilderView = React.lazy(() => import('./components/views/BuilderView').then(m => ({ default: m.BuilderView })));
const CartView = React.lazy(() => import('./components/views/CartView').then(m => ({ default: m.CartView })));
const TrackingView = React.lazy(() => import('./components/views/TrackingView').then(m => ({ default: m.TrackingView })));
import { OrderStatusNotification } from './components/ui/OrderStatusNotification'; 
import { MarketingFloatingWidget } from './components/ui/MarketingFloatingWidget';

import { CameraMonitorProps, RiderMonitorProps } from './types';
import { X } from 'lucide-react';

const RecentOrderModal = () => {
    const { isKioskMode, setIsKioskAttractActive } = useApp();
    const [recentOrderId, setRecentOrderId] = useState<string | null>(null);
    const [mustPayAtCashier, setMustPayAtCashier] = useState(false);

    useEffect(() => {
        const checkOrder = () => {
            const id = localStorage.getItem('last_placed_order_id');
            if (id) {
                const payment = localStorage.getItem('last_placed_order_payment');
                const context = localStorage.getItem('last_placed_order_context');
                setMustPayAtCashier((payment === 'CASH' || payment === 'POS') && (context === 'TAKE_AWAY' || context === 'DINE_IN'));
                
                setRecentOrderId(id);
                localStorage.removeItem('last_placed_order_id');
                localStorage.removeItem('last_placed_order_payment');
                localStorage.removeItem('last_placed_order_context');
            }
        };

        // Check immediately on mount
        checkOrder();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkOrder();
            }
        };

        // Check when returning to the tab/app
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', checkOrder);
        window.addEventListener('order_placed', checkOrder);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', checkOrder);
            window.removeEventListener('order_placed', checkOrder);
        };
    }, []);

    useEffect(() => {
        if (recentOrderId && isKioskMode) {
            const timer = setTimeout(() => {
                setRecentOrderId(null);
                setIsKioskAttractActive(true);
            }, 8000); // Auto close after 8 seconds in Kiosk Mode
            return () => clearTimeout(timer);
        }
    }, [recentOrderId, isKioskMode, setIsKioskAttractActive]);

    if (!recentOrderId) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[99999] flex flex-col items-center justify-center p-6 text-center animate-in fade-in zoom-in duration-300">
            <div className="bg-white p-8 rounded-[2.5rem] shadow-2xl max-w-sm w-full relative overflow-hidden">
                {!isKioskMode && (
                    <button onClick={() => setRecentOrderId(null)} className="absolute top-4 right-4 p-2 bg-gray-100 text-gray-500 rounded-full hover:bg-gray-200 transition-colors">
                        <X size={24} />
                    </button>
                )}
                <div className="flex justify-center mb-6">
                    <CheckCircle2 size={64} className="text-green-500" />
                </div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">Ordine Inviato!</h2>
                
                {mustPayAtCashier && (
                    <div className="bg-red-50 border-2 border-red-500 p-4 rounded-xl mb-4">
                        <p className="text-red-700 font-bold uppercase tracking-wide text-sm mb-1">
                            Azione Richiesta
                        </p>
                        <p className="text-red-600 font-medium leading-snug">
                            Recati in cassa per il pagamento. L'ordine vi verrà preparato solo dopo aver effettuato il pagamento.
                        </p>
                    </div>
                )}

                <p className="text-gray-500 mb-6 leading-relaxed">
                    Il tuo numero d'ordine è:
                </p>
                <div className="bg-gray-100 py-4 px-6 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-300 mb-6">
                    <span className="text-4xl font-black text-indigo-600 tracking-widest">
                        #{recentOrderId.slice(-4).toUpperCase()}
                    </span>
                </div>
                <button 
                    onClick={() => {
                        setRecentOrderId(null);
                        if (isKioskMode) setIsKioskAttractActive(true);
                    }}
                    className="w-full bg-indigo-600 text-white font-bold text-lg py-4 rounded-xl shadow-xl hover:bg-indigo-700 transition-colors"
                >
                    Ho Capito
                </button>
            </div>
        </div>
    );
};

// Lazy Load Admin Components
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminSidebar = React.lazy(() => import('./components/admin/AdminSidebar').then(m => ({ default: m.AdminSidebar })));
const DeliveryManager = React.lazy(() => import('./components/admin/DeliveryManager').then(m => ({ default: m.DeliveryManager })));
const GraphicsManager = React.lazy(() => import('./components/admin/GraphicsManager').then(m => ({ default: m.GraphicsManager })));
const OpeningHoursManager = React.lazy(() => import('./components/admin/OpeningHoursManager').then(m => ({ default: m.OpeningHoursManager })));
const StructureManager = React.lazy(() => import('./components/admin/StructureManager').then(m => ({ default: m.StructureManager })));
const ProductManager = React.lazy(() => import('./components/admin/ProductManager').then(m => ({ default: m.ProductManager })));
const IngredientManager = React.lazy(() => import('./components/admin/IngredientManager').then(m => ({ default: m.IngredientManager })));
const CustomerManager = React.lazy(() => import('./components/admin/CustomerManager').then(m => ({ default: m.CustomerManager })));
const LiveOrderList = React.lazy(() => import('./components/admin/LiveOrderList').then(m => ({ default: m.LiveOrderList })));
const IntercomSystem = React.lazy(() => import('./components/ui/IntercomSystem').then(m => ({ default: m.IntercomSystem })));
const RiderTracker = React.lazy(() => import('./components/admin/RiderTracker').then(m => ({ default: m.RiderTracker })));
const CameraManager = React.lazy(() => import('./components/admin/CameraManager').then(m => ({ default: m.CameraManager })));
const CameraMonitor = React.lazy(() => import('./components/ui/CameraMonitor').then(m => ({ default: m.CameraMonitor as React.FC<CameraMonitorProps> })));
const RiderMonitor = React.lazy(() => import('./components/admin/RiderMonitor').then(m => ({ default: m.RiderMonitor as React.FC<RiderMonitorProps> })));
const OrderHistoryManager = React.lazy(() => import('./components/admin/OrderHistoryManager').then(m => ({ default: m.OrderHistoryManager })));
const SuperAdminDashboard = React.lazy(() => import('./components/admin/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })));
const RiderLockedView = React.lazy(() => import('./components/views/RiderLockedView').then(m => ({ default: m.RiderLockedView })));
const TvMenuView = React.lazy(() => import('./components/views/TvMenuView').then(m => ({ default: m.TvMenuView })));
const TvPairingView = React.lazy(() => import('./components/views/TvPairingView').then(m => ({ default: m.TvPairingView })));
const FiscalPrinterManager = React.lazy(() => import('./components/admin/FiscalPrinterManager').then(m => ({ default: m.FiscalPrinterManager }))); 
const IotPrinterManager = React.lazy(() => import('./components/admin/IotPrinterManager').then(m => ({ default: m.IotPrinterManager }))); 
const AnalyticsManager = React.lazy(() => import('./components/admin/AnalyticsManager').then(m => ({ default: m.AnalyticsManager }))); 
const RiderManagement = React.lazy(() => import('./components/admin/RiderManagement'));
const RiderApp = React.lazy(() => import('./components/rider/RiderApp').then(m => ({ default: m.RiderApp })));
import { PwaInstallPrompt } from './components/PwaInstallPrompt';

// Lazy Load Settings Sections
const GeneralSettingsSection = React.lazy(() => import('./components/admin/settings/GeneralSettingsSection').then(m => ({ default: m.GeneralSettingsSection })));
const MarketingSection = React.lazy(() => import('./components/admin/settings/MarketingSection').then(m => ({ default: m.MarketingSection })));
const SystemToolsSection = React.lazy(() => import('./components/admin/settings/SystemToolsSection').then(m => ({ default: m.SystemToolsSection })));

// --- ERROR BOUNDARY ---
interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // Explicitly defining props to avoid TS errors in strict environments
  readonly props: Readonly<ErrorBoundaryProps>;
  readonly state: ErrorBoundaryState;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.props = props;
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-6 text-white text-center">
          <ShieldAlert size={64} className="text-red-500 mb-6" />
          <h1 className="text-2xl font-bold mb-2">Qualcosa è andato storto</h1>
          <p className="text-gray-400 mb-6 max-w-md text-sm">
            Si è verificato un errore imprevisto. Prova a ricaricare la pagina.
          </p>
          <div className="bg-black/50 p-4 rounded-xl border border-gray-700 mb-6 text-left w-full max-w-md overflow-auto max-h-40">
             <code className="text-xs text-red-300 font-mono">
                {this.state.error?.message || "Unknown Error"}
             </code>
          </div>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-3 bg-indigo-600 rounded-xl font-bold hover:bg-indigo-500 flex items-center gap-2"
          >
            <RefreshCw size={18}/> Ricarica Pagina
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Loading Component
const FullScreenLoader = ({ message, onReset }: { message?: string; onReset?: () => void }) => {
    const [showEmergency, setShowEmergency] = React.useState(false);

    React.useEffect(() => {
        const timer = setTimeout(() => setShowEmergency(true), 3500);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="fixed inset-0 bg-gray-900 flex flex-col items-center justify-center z-50 p-6 text-center">
            <Loader2 size={48} className="text-emerald-500 animate-spin mb-4" />
            <p className="text-white font-bold text-sm tracking-widest uppercase">{message || "Caricamento..."}</p>
            {showEmergency && (
                <div className="mt-6 flex flex-col items-center gap-2 animate-in fade-in duration-300">
                    <p className="text-xs text-gray-400">Il caricamento richiede più tempo del previsto.</p>
                    <button
                        onClick={() => {
                            if (onReset) onReset();
                            else {
                                localStorage.removeItem('RIDER_ID');
                                window.location.href = '/rider';
                            }
                        }}
                        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs font-semibold rounded-lg border border-gray-700 transition"
                    >
                        Riavvia o Torna all'inizio
                    </button>
                </div>
            )}
        </div>
    );
};

// Suspended View
const SuspendedView = () => (
    <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-24 h-24 bg-red-900/50 rounded-full flex items-center justify-center mb-6 animate-pulse">
            <ShieldAlert size={48} className="text-red-500"/>
        </div>
        <h1 className="text-3xl font-black text-white mb-4 uppercase tracking-widest">Servizio Sospeso</h1>
        <p className="text-gray-400 max-w-sm mb-8 leading-relaxed">
            Il servizio per questa attività è momentaneamente non disponibile. Contatta l'amministrazione per maggiori informazioni.
        </p>
        <div className="text-xs font-mono text-gray-600">ERROR_CODE: TENANT_INACTIVE</div>
    </div>
);

// --- TOAST NOTIFICATION COMPONENT ---
const ToastNotification = () => {
    const { toast } = useApp();
    if (!toast.show) return null;
    return (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-6 py-4 rounded-2xl shadow-2xl z-[9999] animate-in fade-in slide-in-from-bottom-4 flex items-center gap-3 border border-gray-700/50 backdrop-blur-md">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.5)]"></div>
            <span className="text-sm font-bold tracking-wide">{toast.message}</span>
        </div>
    );
};

// --- DYNAMIC TENANT META & MANIFEST MANAGER ---
const TenantMetaManager = () => {
    const { settings, graphicsSettings, view, settingsLoaded, graphicsLoaded } = useApp();
    const colorsString = JSON.stringify(graphicsSettings.appColors);
    const [platformUpdateTick, setPlatformUpdateTick] = useState(0);

    useEffect(() => {
        const handlePlatformUpdate = () => setPlatformUpdateTick(t => t + 1);
        window.addEventListener('platform_graphics_updated', handlePlatformUpdate);
        return () => window.removeEventListener('platform_graphics_updated', handlePlatformUpdate);
    }, []);

    useEffect(() => {
        const tenantId = getTenantId();
        
        // PREVENT FLICKERING: If settings are not yet fully loaded from Firestore, skip update
        // This ensures the logo doesn't reset to default during the initial handshake
        if (tenantId && tenantId !== 'root' && (!settingsLoaded || !graphicsLoaded)) return;

        const DEFAULT_ICON = "https://cdn-icons-png.flaticon.com/512/1046/1046747.png"; // Utensils instead of shopper
        const RIDER_ICON = "https://cdn-icons-png.flaticon.com/512/7541/7541900.png"; // Scooter Icon
        
        // ICON MAP (Must match ICONS_MAP in Shared.tsx roughly)
        const ICON_MAP: Record<string, string> = {
            'UtensilsCrossed': 'https://cdn-icons-png.flaticon.com/512/1046/1046747.png',
            'IceCream': 'https://cdn-icons-png.flaticon.com/512/938/938063.png',
            'Pizza': 'https://cdn-icons-png.flaticon.com/512/1404/1404945.png',
            'Coffee': 'https://cdn-icons-png.flaticon.com/512/751/751621.png',
            'Beer': 'https://cdn-icons-png.flaticon.com/512/931/931949.png',
            'Wine': 'https://cdn-icons-png.flaticon.com/512/2405/2405451.png',
            'Croissant': 'https://cdn-icons-png.flaticon.com/512/992/992762.png',
            'ChefHat': 'https://cdn-icons-png.flaticon.com/512/1830/1830839.png',
            'ShoppingBag': 'https://cdn-icons-png.flaticon.com/512/1046/1046747.png'
        };

        let logoUrl = DEFAULT_ICON;
        let appName = "Ordina";
        let startUrl = "/";
        let themeColor = "#4f46e5";
        let bgColor = "#111827";

        // RIDER APP OVERRIDE
        const isRiderApp = view === 'rider' || view === 'rider-locked' || window.location.pathname.includes('/rider');

        if (isRiderApp) {
            appName = "Rider App";
            logoUrl = RIDER_ICON;
            themeColor = "#f97316"; // Orange for Rider
            bgColor = "#1f2937";
            
            if (window.location.pathname.includes('/rider')) {
                startUrl = window.location.origin + "/rider";
            } else if (view === 'rider-locked') {
                const shop = getTenantId();
                startUrl = `${window.location.origin}/?shop=${shop}&view=rider-locked`;
            } else {
                startUrl = window.location.origin + "/rider";
            }
        } 
        else if (tenantId && tenantId !== 'root') {
            appName = settings.appName || "Ordina";
            themeColor = graphicsSettings.appColors?.primary || "#4f46e5";
            bgColor = graphicsSettings.appColors?.background || "#111827";
            startUrl = `${window.location.origin}/?shop=${tenantId}`; 

            if (graphicsSettings.appLogoType === 'IMAGE' && graphicsSettings.customLogoUrl) {
                logoUrl = graphicsSettings.customLogoUrl;
            } else if (graphicsSettings.appLogoIcon && ICON_MAP[graphicsSettings.appLogoIcon]) {
                logoUrl = ICON_MAP[graphicsSettings.appLogoIcon];
            }
        } else {
            // LANDING PAGE (Root / No Tenant)
            startUrl = window.location.origin + "/";
            const cachedPlatformStr = localStorage.getItem('platform_graphics_cache');
            if (cachedPlatformStr) {
                try {
                    const platformMeta = JSON.parse(cachedPlatformStr);
                    appName = platformMeta.title || "OrdinaSmart";
                    if (platformMeta.customLogoUrl || platformMeta.logo) {
                        logoUrl = platformMeta.customLogoUrl || platformMeta.logo;
                    }
                } catch(e){}
            } else {
                return; // Do NOT generate manifest/icons if platform graphics aren't loaded yet! Wait for platformUpdateTick.
            }
        }

        if (document.title !== appName) document.title = appName;

        const updateLink = (id: string, rel: string, href: string) => {
            let link = document.getElementById(id) as HTMLLinkElement;
            const finalHref = href;
            if (!link) {
                link = document.createElement('link');
                link.id = id;
                link.rel = rel;
                document.head.appendChild(link);
            }
            if (link.href !== finalHref) link.href = finalHref;
        };

        updateLink('dynamic-favicon', 'icon', logoUrl);
        updateLink('dynamic-apple-icon', 'apple-touch-icon', logoUrl);
        updateLink('dynamic-shortcut', 'shortcut icon', logoUrl);

        const updateMeta = (id: string, property: string, content: string) => {
            let meta = document.getElementById(id) as HTMLMetaElement;
            if (!meta) {
                // Try finding by property if id is not found (some scrapers ignore id)
                meta = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`) as HTMLMetaElement;
                if (!meta) {
                    meta = document.createElement('meta');
                    meta.id = id;
                    if (property.startsWith('og:')) meta.setAttribute('property', property);
                    else meta.setAttribute('name', property);
                    document.head.appendChild(meta);
                } else {
                    meta.id = id; // Enforce our ID for future updates
                }
            }
            if (meta.content !== content) meta.content = content;
        };

        updateMeta('meta-apple-title', 'apple-mobile-web-app-title', appName);
        updateMeta('og-title', 'og:title', appName);
        updateMeta('og-site_name', 'og:site_name', appName);
        updateMeta('og-image', 'og:image', logoUrl);
        updateMeta('og-description', 'og:description', isRiderApp ? 'App dedicata ai rider per la gestione consegne.' : `Ordina online da ${appName}`);
        updateMeta('og-url', 'og:url', window.location.href);

        const manifestContent = {
            name: appName,
            short_name: appName.length > 12 ? appName.substring(0, 12) : appName,
            start_url: startUrl, 
            display: "standalone",
            background_color: bgColor,
            theme_color: themeColor,
            icons: [
                { src: logoUrl, sizes: "192x192", type: "image/png", purpose: "any" },
                { src: logoUrl, sizes: "192x192", type: "image/png", purpose: "maskable" },
                { src: logoUrl, sizes: "512x512", type: "image/png", purpose: "any" },
                { src: logoUrl, sizes: "512x512", type: "image/png", purpose: "maskable" }
            ]
        };
        
        const encodedData = encodeURIComponent(JSON.stringify(manifestContent));
        let manifestURL = `data:application/manifest+json;charset=utf-8,${encodedData}`;
        try {
            const blob = new Blob([JSON.stringify(manifestContent)], {type: 'application/json'});
            manifestURL = URL.createObjectURL(blob);
        } catch(e){}
        
        let manifestLink = document.getElementById('dynamic-manifest') as HTMLLinkElement;
        if (!manifestLink) {
            manifestLink = document.createElement('link');
            manifestLink.id = 'dynamic-manifest';
            manifestLink.rel = 'manifest';
            document.head.appendChild(manifestLink);
        }
        // If it was a blob URL previously, revoke it just in case
        if (manifestLink.href && manifestLink.href.startsWith('blob:')) URL.revokeObjectURL(manifestLink.href);
        if (manifestLink.href !== manifestURL) manifestLink.href = manifestURL;

    }, [settings.appName, graphicsSettings.customLogoUrl, graphicsSettings.appLogoIcon, graphicsSettings.appLogoType, colorsString, view, platformUpdateTick]);

    return null;
};

// Main Router Component - Consumes AppContext
const AppContent = () => {
  const { 
    view, setView, adminSubView, setAdminSubView, user, currentUserTenantId,
    liveOrders, settings,
    products, ingredients, menus, categories, deliverySettings, graphicsSettings,
    playNotificationSound, setIsOrderListOpen, initialLoading,
    isProRetailMode, isDeviceMuted,
    pendingOrdersCount,
    setTrackingOrderId,
    trackingOrderId,
    isSuperUser,
    isKioskAttractActive,
    setIsKioskAttractActive,
    isKioskMode,
    settingsLoaded
  } = useApp();

  const adminLang = localStorage.getItem('language_' + currentUserTenantId) as Language || 'it';
  const t = (key: string) => getTranslation(adminLang, key as any);

  useIotAutoPrint();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
      const scroll = () => {
          if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollTop = 0;
          }
      };
      // Try immediately
      scroll();
      // Try again after a delay for lazy loaded content
      const timer = setTimeout(scroll, 200);
      return () => clearTimeout(timer);
  }, [adminSubView]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [knownOrderIds, setKnownOrderIds] = useState<Set<string>>(new Set());
  const [isAdminSidebarOpen, setIsAdminSidebarOpen] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
      setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
      if (!touchStart || !touchEnd) return;
      const distance = touchStart - touchEnd;
      const isSwipeRight = distance < -50;
      // If swipe started from left edge (< 40px) and moved right
      if (isSwipeRight && touchStart < 40) {
          setIsAdminSidebarOpen(true);
      }
  };
  
  const [secretClicks, setSecretClicks] = useState(0);
  const [isSuperAdminOpen, setIsSuperAdminOpen] = useState(false);
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const [backToHomeClicks, setBackToHomeClicks] = useState(0);
  const backToHomeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isStartupDelayPassed, setIsStartupDelayPassed] = useState(false);
  const [isTenantBlocked, setIsTenantBlocked] = useState(false);
  const [isDomainCheckLoading, setIsDomainCheckLoading] = useState(false);

  // NEW: Ref to track manual navigation to prevent auto-redirect loop
  const hasAutoRedirected = useRef(false);

  const urlParams = useMemo(() => new URLSearchParams(window.location.search), [window.location.search]);
  const shopParam = urlParams.get('shop');
  const viewParam = urlParams.get('view');
  const trackId = urlParams.get('trackOrder');
  const hostname = window.location.hostname;

  const pathTrackParams = useMemo(() => {
      const pathname = window.location.pathname;
      if (pathname.startsWith('/track/')) {
          const parts = pathname.split('/');
          if (parts.length >= 4) {
              return { tenantId: parts[2], orderId: parts[3] };
          }
      }
      return null;
  }, [window.location.pathname]);

  const SUPER_ADMIN_B64 = "bm9zXzFAaWNsb3VkLmNvbQ==";

  useEffect(() => {
      const pairCode = urlParams.get('pair');
      if (pairCode && user && currentUserTenantId && currentUserTenantId !== 'root') {
          // Prevent multiple executions
          if ((window as any).tvPairingAttempted) return;
          (window as any).tvPairingAttempted = true;

          const confirmPair = async () => {
              if (window.confirm(`Vuoi associare questa TV (Codice: ${pairCode}) al tuo negozio?`)) {
                  try {
                      const { doc, updateDoc } = await import('firebase/firestore');
                      const { db } = await import('./firebase');
                      await updateDoc(doc(db, 'tvPairingRequests', pairCode.toUpperCase()), {
                          status: 'paired',
                          tenantId: currentUserTenantId
                      });
                      alert('TV associata con successo! Guarda lo schermo della TV.');
                  } catch (e) {
                      console.error(e);
                      alert('Errore durante l\'associazione TV. Verifica il codice e riprova.');
                  }
              }
              // Clean up the URL parameter
              const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
              window.history.replaceState({path: newUrl}, '', newUrl);
          };
          confirmPair();
      }
  }, [user, currentUserTenantId, urlParams]);

  useEffect(() => {
      let currentTenant = getTenantId();
      if (!currentTenant && currentUserTenantId === 'root') currentTenant = 'root';
      
      if (currentTenant === 'root' && view === 'admin') setIsSuperAdminOpen(true);
  }, [currentUserTenantId, view]);

  useEffect(() => {
      // urlParams, shopParam, etc are now available from useMemo
      const isRiderPath = window.location.pathname === '/rider' || window.location.pathname === '/rider/';

      if (window.location.pathname === '/tv' || window.location.pathname === '/tv/') {
          setView('tv-pairing');
          setIsDomainCheckLoading(false);
          setIsStartupDelayPassed(true);
          return;
      }

      if (isRiderPath) {
          setView('rider');
          setIsDomainCheckLoading(false);
          setIsStartupDelayPassed(true);
          return;
      }

      // Fix: If we are NOT on the /rider path and NOT requesting rider-locked view,
      // but the saved view is 'rider' or 'rider-locked', reset to 'intro' 
      // so the user sees the landing page or the shop intro.
      if (!isRiderPath && viewParam !== 'rider-locked' && (view === 'rider' || view === 'rider-locked')) {
          setView('intro');
      }

      if (viewParam === 'rider-locked') {
          if (shopParam) localStorage.setItem('APP_TENANT_ID', shopParam);
          localStorage.setItem('isRiderDevice', 'true');
          setView('rider-locked');
          setIsDomainCheckLoading(false);
          setIsStartupDelayPassed(true);
          return;
      }

      if (!hostname.includes('localhost')) {
          setIsDomainCheckLoading(true); 
          const safetyTimer = setTimeout(() => {
              console.warn("Domain check timeout - Forcing unlock");
              setIsDomainCheckLoading(false);
          }, 3000); // Reduced timeout for faster startup

          resolveTenantIdFromDomain(hostname)
            .then(resolvedId => {
              if (resolvedId) {
                  const currentStored = localStorage.getItem('APP_TENANT_ID');
                  if (currentStored !== resolvedId) {
                      localStorage.setItem('APP_TENANT_ID', resolvedId);
                      window.location.reload(); 
                  } else {
                      clearTimeout(safetyTimer);
                      setIsDomainCheckLoading(false);
                  }
              } else {
                  if (!shopParam && !trackId && !urlParams.get('pair') && window.location.pathname === '/' && localStorage.getItem('APP_TENANT_ID')) {
                      // Se l'utente visita la landing page, puliamo la memoria del tenant visitato precedentemente
                      if (localStorage.getItem('APP_TENANT_ID') !== 'root') {
                          localStorage.removeItem('APP_TENANT_ID');
                          localStorage.removeItem('root_app_view');
                          // Removed window.location.reload() to prevent loop
                          return;
                      }
                  }
                  clearTimeout(safetyTimer);
                  setIsDomainCheckLoading(false);
              }
            })
            .catch(e => {
                console.error("Domain resolution error:", e);
                clearTimeout(safetyTimer);
                setIsDomainCheckLoading(false);
            });
      } else {
          if (!shopParam && !trackId && !pathTrackParams && !urlParams.get('pair') && window.location.pathname === '/' && localStorage.getItem('APP_TENANT_ID')) {
              if (localStorage.getItem('APP_TENANT_ID') !== 'root') {
                  localStorage.removeItem('APP_TENANT_ID');
                  localStorage.removeItem('root_app_view');
                  // Removed window.location.reload() to prevent loop
                  return;
              }
          }
      }

      const actualShop = shopParam || (pathTrackParams ? pathTrackParams.tenantId : null);
      const actualTrackId = trackId || (pathTrackParams ? pathTrackParams.orderId : null);

      if (actualShop) {
          localStorage.setItem('APP_TENANT_ID', actualShop);
          // Fix: If navigating to a specific shop, ensure we don't show the Rider App or Login screen
          // This fixes the issue where ?shop=example would show Rider App or Login if previously visited
          if (!viewParam && !trackId && !pathTrackParams && (view === 'rider' || view === 'rider-locked' || view === 'login')) {
              setView('intro');
          }
      } else if (isMainDomainLanding && !viewParam && !trackId && !pathTrackParams && !urlParams.get('pair') && (view === 'admin' || view === 'login')) {
          // Fix: If navigating to landing page, never show admin or login from cache
          setView('intro');
      }

      if (urlParams.get('pair')) setView('admin');
      else if (viewParam === 'admin') setView('admin');
      // removed super-admin view
      else if (viewParam === 'rider') setView('rider');
      else if (viewParam === 'tv-menu') setView('tv-menu');
      else if (actualTrackId) {
          setTrackingOrderId(actualTrackId);
          setView('tracking');
      }

      const checkTenantStatusAndHistory = async () => {
          const currentTenantId = getTenantId();
          if (currentTenantId && currentTenantId !== 'root') {
              const meta = await getTenantMetadata(currentTenantId);
              if (meta) {
                  let isBlocked = meta.isActive === false;
                  
                  // Verifica dinamica scadenza Abbonamento/Timer
                  if (meta.subscriptionTimerEnabled && meta.subscriptionStartDate && meta.subscriptionDaysLimit) {
                      const daysPassed = Math.floor((Date.now() - meta.subscriptionStartDate) / (1000 * 60 * 60 * 24));
                      if (daysPassed >= meta.subscriptionDaysLimit) {
                          isBlocked = true;
                      }
                  }

                  if (isBlocked) setIsTenantBlocked(true);
                  addVisitedTenant(meta.id, meta.name);
              }
          }
      };
      checkTenantStatusAndHistory();

      const timer = setTimeout(() => setIsStartupDelayPassed(true), 800); 
      return () => clearTimeout(timer);
  }, [currentUserTenantId]);

  const activeTenantId = getTenantId() || currentUserTenantId || '';
  const isRiderPath = window.location.pathname === '/rider' || window.location.pathname === '/rider/';
  const isTvPath = window.location.pathname === '/tv' || window.location.pathname === '/tv/';
  const isMainDomainLanding = hostname.includes('run.app') || hostname.includes('localhost') || hostname.includes('ordina-ora.it') || hostname.includes('ordinasmart.app');

  const shouldShowLanding = (!activeTenantId || (isMainDomainLanding && !shopParam && !pathTrackParams && !urlParams.get('pair'))) && 
                           !trackingOrderId && 
                           view !== 'login' && 
                           view !== 'admin' && 
                           view !== 'rider-locked' && 
                           !isRiderPath && 
                           !isTvPath && 
                           view !== 'rider' &&
                           view !== 'tv-pairing' &&
                           view !== 'tv-menu';

  // AUTO-REDIRECT TO ADMIN (If logged in)
  useEffect(() => {
      // Reset flag if user logs out
      if (!user) {
          hasAutoRedirected.current = false;
      }
      
      // Only redirect if valid user AND not currently loading AND we haven't already redirected this session
      // This allows the admin to manually click "View Client App" (setting view='intro') without being forced back.
      if (!initialLoading && user && currentUserTenantId && view === 'intro' && !hasAutoRedirected.current && !shouldShowLanding) {
          hasAutoRedirected.current = true;
          setView('admin');
      }
  }, [user, currentUserTenantId, view, initialLoading]);

  useEffect(() => {
      const tid = getTenantId();
      if (tid && settings.appName && tid !== 'root') {
          addVisitedTenant(tid, settings.appName);
      }
  }, [settings.appName]);

  const handleReturnToSuperAdmin = () => {
      localStorage.setItem('APP_TENANT_ID', 'root');
      localStorage.setItem('app_view', 'admin'); 
      window.location.href = '/?shop=root&view=admin';
  };

  const handleStopRiderImpersonation = () => {
      localStorage.removeItem('RIDER_ID');
      window.dispatchEvent(new CustomEvent('rider-id-change', { detail: { riderId: null } }));
      
      // Ripristiniamo la dashboard Super Admin principale
      localStorage.setItem('APP_TENANT_ID', 'root');
      localStorage.setItem('app_view', 'admin');
      
      const url = new URL(window.location.href);
      url.searchParams.set('shop', 'root');
      url.searchParams.set('view', 'admin');
      try { window.history.replaceState({ view: 'admin' }, '', url.toString()); } catch(e){}
      
      window.dispatchEvent(new Event('tenant-changed'));
      setView('admin');
      setIsSuperAdminOpen(true);
  };

  // --- HELPER: VERIFY SUPER ADMIN ---
  const verifySuperAdmin = async (email: string) => {
    const normEmail = email.toLowerCase().trim();
    const SUPER_ADMIN_B64 = "bm9zX0Bob3RtYWlsLml0"; // nos_@hotmail.it
    const isHardcoded = normEmail === "nos_@hotmail.it" || normEmail === "nos_1@icloud.com" || btoa(normEmail) === SUPER_ADMIN_B64;
    if (isHardcoded) return true;

    try {
      const q = query(collection(db, 'super_admins'), where('email', '==', normEmail));
      const snap = await getDocs(q);
      return !snap.empty;
    } catch (e) {
      console.error("SuperAdmin check failed:", e);
      return false;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoginError('');
      setIsLoggingIn(true);

      try {
          // 1. Firebase Authentication
          const credential = await signInWithEmailAndPassword(auth, email, password);
          const userEmail = credential.user.email;
          if (!userEmail) throw new Error("Email non valida restituita dal provider.");
          
          // NORMALIZE EMAIL (Crucial for DB lookup case-sensitivity)
          const normEmail = userEmail.toLowerCase().trim();

          // 2. Check Super Admin
          const isSuper = await verifySuperAdmin(normEmail);
          
          if (isSuper) {
              const url = new URL(window.location.href);
              const existingShop = url.searchParams.get('shop');
              const targetShop = (existingShop && existingShop !== 'root') ? existingShop : 'root';
              
              localStorage.setItem('APP_TENANT_ID', targetShop);
              localStorage.setItem('app_view', 'admin');
              
              url.searchParams.set('shop', targetShop);
              url.searchParams.set('view', 'admin');
              try { window.history.replaceState({ view: 'admin' }, '', url.toString()); } catch(e){}
              
              window.dispatchEvent(new Event('tenant-changed'));
              setView('admin');
              setIsLoggingIn(false);
              return;
          }

          // 3. Check Tenant Admin
          const assignedTenantId = await getAdminTenant(normEmail);
          
          if (assignedTenantId) {
              localStorage.setItem('APP_TENANT_ID', assignedTenantId);
              localStorage.setItem('app_view', 'admin');
              
              const url = new URL(window.location.href);
              url.searchParams.set('shop', assignedTenantId);
              url.searchParams.set('view', 'admin');
              try { window.history.replaceState({ view: 'admin' }, '', url.toString()); } catch(e){}
              
              window.dispatchEvent(new Event('tenant-changed'));
              setView('admin');
              setIsLoggingIn(false);
              return;
          } else {
              throw new Error("Nessun negozio associato a questo account.");
          }

      } catch (err: any) {
          setIsLoggingIn(false);
          await signOut(auth).catch(() => {}); // Cleanup partial auth
          
          console.error("Login Error:", err);
          let errorMessage = "Errore durante l'accesso.";
          if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
              errorMessage = "Email o Password errati.";
          } else if (err.code === 'auth/too-many-requests') {
              errorMessage = "Troppi tentativi. Riprova più tardi.";
          } else if (err.message) {
              errorMessage = err.message;
          }
          setLoginError(errorMessage);
      }
  };

  const handleForgotPassword = async () => {
      const targetEmail = user?.email || email;
      if (!targetEmail) {
          alert("Inserisci la tua email nel campo 'Email' per ricevere il link di reset.");
          return;
      }
      if (confirm(`Vuoi inviare un'email per reimpostare la password all'indirizzo: ${targetEmail}?`)) {
          try {
              await sendPasswordResetEmail(auth, targetEmail);
              alert("Email di reset inviata! Controlla la tua posta.");
          } catch (e: any) {
              alert("Errore: " + e.message);
          }
      }
  };

  const handleSmartBackToHome = () => {
      const newCount = backToHomeClicks + 1;
      setBackToHomeClicks(newCount);
      if (backToHomeTimer.current) clearTimeout(backToHomeTimer.current);

      if (newCount >= 3) {
          setBackToHomeClicks(0);
          const currentTenant = getTenantId();
          if (currentTenant === 'root') {
              if (confirm("🛠️ SYSTEM: Uscire dalla modalità ROOT?")) {
                  localStorage.removeItem('APP_TENANT_ID');
                  window.location.href = window.location.origin;
              }
          } else {
              if(confirm("🛠️ SYSTEM: Accedere come ROOT?")) {
                  localStorage.setItem('APP_TENANT_ID', 'root');
                  window.location.href = '/?shop=root&view=login';
              }
          }
      } else {
          backToHomeTimer.current = setTimeout(() => {
              setBackToHomeClicks(0);
              const activeTenantId = getTenantId();
              if (activeTenantId && activeTenantId !== 'root') setView('intro');
              else {
                  localStorage.removeItem('APP_TENANT_ID');
                  const url = new URL(window.location.href);
                  url.searchParams.delete('shop');
                  url.searchParams.delete('view');
                  try { window.history.replaceState({}, '', url.pathname + url.search); } catch (e) {}
                  setView('intro'); 
              }
          }, 300);
      }
  };

  const handlePressStart = () => {
      if (pressTimer.current) clearTimeout(pressTimer.current);
      pressTimer.current = setTimeout(() => {
          setIsSuperAdminOpen(true);
          setSecretClicks(0); 
          if (navigator.vibrate) navigator.vibrate(200);
      }, 7000); 
  };

  const handlePressEnd = () => {
      if (pressTimer.current) {
          clearTimeout(pressTimer.current);
          pressTimer.current = null;
      }
  };

  const handleSecretGearClick = async () => {
      const newCount = secretClicks + 1;
      if (newCount >= 15) { 
          const newMode = !isProRetailMode; 
          const currentTenant = getTenantId() || "ROOT";
          if (!confirm(`MODIFICA MODALITÀ PER: ${currentTenant}\n\nAttuale: ${isProRetailMode ? 'RETAIL' : 'RISTORANTE'}\nNuova: ${newMode ? 'RETAIL' : 'RISTORANTE'}\n\nConfermi il cambio?`)) {
              setSecretClicks(0);
              return;
          }
          try {
              await setGlobalProRetailMode(newMode);
              alert(`SUCCESSO!\n\nImpostato ${newMode ? 'RETAIL' : 'RISTORANTE'} per ${currentTenant}.\n\nL'app si riavvierà ora.`);
              setTimeout(() => {
                  const baseUrl = window.location.origin + window.location.pathname;
                  const shopParam = getTenantId();
                  window.location.href = shopParam && shopParam !== 'root' ? `${baseUrl}?shop=${shopParam}` : baseUrl;
              }, 500);
          } catch (e: any) { alert(`Errore cambio modalità: ${e.message}`); }
          setSecretClicks(0);
      } else {
          setSecretClicks(newCount);
      }
  };

  useEffect(() => {
    if (!isStartupDelayPassed || !user) return; 
    const currentTenant = getTenantId();
    if (currentUserTenantId && currentTenant && currentUserTenantId !== 'root' && currentUserTenantId !== currentTenant) return;
    if (view === 'rider-locked' || view === 'rider') return;

    const pending = liveOrders.filter(o => o && o.status === 'pending');
    const newArrivals = pending.filter(o => o && !knownOrderIds.has(o.id));
    
    if (newArrivals.length > 0) {
      setKnownOrderIds(prev => {
        const next = new Set(prev);
        newArrivals.forEach(o => o && next.add(o.id));
        return next;
      });
      if (!isDeviceMuted) playNotificationSound();
      setIsOrderListOpen(true); 
    }
  }, [liveOrders, user, isStartupDelayPassed, currentUserTenantId, isDeviceMuted, view]);

  const [platformGraphics, setPlatformGraphics] = useState<any>(() => {
      if (typeof window !== 'undefined') {
          const cached = localStorage.getItem('platform_graphics_cache');
          return cached ? JSON.parse(cached) : null;
      }
      return null;
  });

  useEffect(() => {
      const loadPlatform = async () => {
          try {
              const { getPlatformGraphics } = await import('./services/dbService');
              const g = await getPlatformGraphics();
              if (g) {
                  setPlatformGraphics(g);
                  localStorage.setItem('platform_graphics_cache', JSON.stringify(g));
                  window.dispatchEvent(new Event('platform_graphics_updated'));
              }
          } catch (e) {
              console.error(e);
          }
      };
      if (!activeTenantId || activeTenantId === 'root' || (isMainDomainLanding && !shopParam)) {
          loadPlatform();
      }
  }, [activeTenantId, isMainDomainLanding, shopParam]);

  if (isTenantBlocked && !isSuperAdminOpen) {
      return (
          <>
            <SuspendedView />
            <div className="fixed bottom-0 right-0 w-20 h-20 z-[100]" onMouseDown={handlePressStart} onMouseUp={handlePressEnd} onMouseLeave={handlePressEnd} onTouchStart={handlePressStart} onTouchEnd={handlePressEnd} />
            {isSuperAdminOpen && <React.Suspense fallback={null}><SuperAdminDashboard onClose={() => setIsSuperAdminOpen(false)} /></React.Suspense>}
          </>
      );
  }

  if (initialLoading || isDomainCheckLoading) {
      const isLandingPageLoader = !activeTenantId || activeTenantId === 'root' || (isMainDomainLanding && !shopParam);

      const getLoaderTitle = () => {
          if (isDomainCheckLoading) return "Verifica Dominio...";
          if (isRiderPath || view === 'rider' || view === 'rider-locked') return "OrdinaSmart Rider";
          if (isLandingPageLoader) {
              return platformGraphics?.title || "OrdinaSmart";
          }
          // For tenants, avoid showing the wrong cached name by waiting for load,
          // or at least fallback to OrdinaSmart if not loaded to prevent previous tenant name flash
          if (!settingsLoaded && settings.appName === 'Ordina Ora') return "OrdinaSmart"; 
          return settings.appName || "Caricamento...";
      };

      const getLoaderIcon = () => {
          if (isRiderPath || view === 'rider' || view === 'rider-locked') {
              return <img src="https://cdn-icons-png.flaticon.com/512/7541/7541900.png" alt="Rider Logo" className="w-16 h-16 object-contain animate-bounce" />;
          }

          if (isLandingPageLoader) {
              const pLogo = platformGraphics?.customLogoUrl || platformGraphics?.logo;
              if (pLogo) {
                  return <img src={pLogo} alt="Platform Logo" className="w-[85%] h-[85%] object-contain animate-bounce scale-110" />;
              }
              // STRICTLY DO NOT fallback to tenant graphicsSettings if on landing page
              return <Sparkles size={48} className="text-white animate-bounce" />;
          }

          if (graphicsSettings?.appLogoType === 'IMAGE' && graphicsSettings?.customLogoUrl) {
              return <img src={graphicsSettings.customLogoUrl} alt="Logo" className="w-[85%] h-[85%] object-contain animate-bounce scale-110" />;
          }

          const ICON_MAP: Record<string, string> = {
              'UtensilsCrossed': 'https://cdn-icons-png.flaticon.com/512/1046/1046747.png',
              'IceCream': 'https://cdn-icons-png.flaticon.com/512/938/938063.png',
              'Pizza': 'https://cdn-icons-png.flaticon.com/512/1404/1404945.png',
              'Coffee': 'https://cdn-icons-png.flaticon.com/512/751/751621.png',
              'Beer': 'https://cdn-icons-png.flaticon.com/512/931/931949.png',
              'Wine': 'https://cdn-icons-png.flaticon.com/512/2405/2405451.png',
              'Croissant': 'https://cdn-icons-png.flaticon.com/512/992/992762.png',
              'ChefHat': 'https://cdn-icons-png.flaticon.com/512/1830/1830839.png',
              'ShoppingBag': 'https://cdn-icons-png.flaticon.com/512/1046/1046747.png' // Fallback to utensils if ShoppingBag is selected
          };
          
          const mappedIcon = graphicsSettings?.appLogoIcon ? ICON_MAP[graphicsSettings.appLogoIcon] : null;

          if (mappedIcon) {
              return <img src={mappedIcon} alt="Icon" className="w-16 h-16 object-contain animate-bounce" />;
          }

          return <img src="https://cdn-icons-png.flaticon.com/512/1046/1046747.png" alt="Icon" className="w-16 h-16 object-contain animate-bounce" />;
      };

      return (
        <div className="fixed inset-0 bg-[#4f46e5] z-[9999] flex flex-col items-center justify-center p-6 text-center">
            <div className="relative mb-8">
                <div className="absolute inset-0 bg-white/20 rounded-full blur-2xl animate-pulse"></div>
                <div className="relative w-24 h-24 bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 flex items-center justify-center shadow-2xl animate-in zoom-in duration-500 overflow-hidden">
                    {getLoaderIcon()}
                </div>
            </div>
            
            <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
                <h2 className="text-white font-black text-2xl tracking-tight">
                    {getLoaderTitle()}
                </h2>
                <div className="flex items-center justify-center gap-2 text-white/60 text-sm font-medium">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Preparazione...</span>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="w-48 h-1 bg-white/10 rounded-full mt-8 overflow-hidden">
                <div className="h-full bg-white rounded-full animate-progress-indefinite"></div>
            </div>
            
            <style>{`
                @keyframes progress-indefinite {
                    0% { width: 0%; transform: translateX(-100%); }
                    50% { width: 30%; }
                    100% { width: 0%; transform: translateX(400%); }
                }
                .animate-progress-indefinite {
                    animation: progress-indefinite 2s infinite linear;
                }
            `}</style>
        </div>
      );
  }

  let content = null;
  
  // Kiosk Mode Attract Screen is now rendered separately in the return statement
  if (shouldShowLanding) {
      content = <LandingView />;
  } else if (isRiderPath || view === 'rider') {
      content = <React.Suspense fallback={<FullScreenLoader message="caricamento app Rider" />}><RiderApp /></React.Suspense>;
  } else if (view === 'intro') {
      // FIX: If we are logged in as admin, but still in 'intro' view, show a loader 
      // instead of the client app to prevent the "flash" before the auto-redirect to admin.
      if (user && currentUserTenantId && !hasAutoRedirected.current) {
          content = <div className="fixed inset-0 bg-gray-900 flex flex-col items-center justify-center z-50">
              <Loader2 size={48} className="text-indigo-500 animate-spin mb-4" />
              <p className="text-white font-bold text-sm tracking-widest uppercase">Accesso Area Admin...</p>
          </div>;
      } else {
          content = <IntroView />;
      }
  } else if (view === 'menu-type') {
      content = <MenuSelectionView />;
  } else if (view === 'builder') {
      content = <BuilderView />;
  } else if (view === 'cart') {
      content = <CartView />;
  } else if (view === 'tracking') {
      content = trackingOrderId ? <TrackingView orderId={trackingOrderId} /> : <IntroView/>;
  } else if (view === 'rider') {
      content = <React.Suspense fallback={<FullScreenLoader message="caricamento app Rider" />}><RiderApp /></React.Suspense>;
  } else if (view === 'rider-locked') {
      content = <React.Suspense fallback={<FullScreenLoader message="caricamento app Rider" />}><RiderLockedView /></React.Suspense>;
  } else if (view === 'tv-menu') {
      content = <React.Suspense fallback={<FullScreenLoader message="Caricamento Menu TV..." />}><TvMenuView /></React.Suspense>;
  } else if (view === 'tv-pairing') {
      content = <React.Suspense fallback={<FullScreenLoader message="Associazione TV..." />}><TvPairingView /></React.Suspense>;
  } else if (view === 'login') {
      // --- COMPLETELY REWRITTEN LOGIN VIEW ---
      content = (
          <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6 relative overflow-hidden">
              {/* Background Shapes */}
              <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-indigo-900/20 rounded-full blur-[100px] animate-pulse pointer-events-none" />
              <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-900/20 rounded-full blur-[100px] pointer-events-none" />

              <div className="bg-gray-800 w-full max-w-sm p-8 rounded-[2.5rem] border border-gray-700 shadow-2xl relative z-10 flex flex-col gap-6">
                  
                  {/* Header */}
                  <div className="text-center">
                      <div 
                          onClick={handleSecretGearClick} 
                          onMouseDown={handlePressStart} onMouseUp={handlePressEnd} onMouseLeave={handlePressEnd} onTouchStart={handlePressStart} onTouchEnd={handlePressEnd}
                          className="w-20 h-20 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white cursor-pointer active:scale-95 transition-transform shadow-lg shadow-indigo-900/50"
                      >
                          <SettingsIcon size={32} />
                      </div>
                      <h2 className="text-2xl font-black text-white">Area Partner</h2>
                      <p className="text-gray-400 text-sm mt-1">Accedi al pannello di controllo</p>
                      {activeTenantId && <div className="mt-2 inline-block bg-gray-700/50 px-3 py-1 rounded-full text-[10px] font-mono text-gray-300 border border-gray-600">ID: {activeTenantId}</div>}
                  </div>

                  {/* Error Message */}
                  {loginError && (
                      <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                          <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5"/>
                          <p className="text-red-400 text-xs font-bold leading-tight">{loginError}</p>
                      </div>
                  )}

                  {/* Form */}
                  <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                          <label htmlFor="login-email" className="text-xs font-bold text-gray-500 uppercase ml-2 mb-1 block">Email</label>
                          <input 
                              id="login-email" 
                              name="email" 
                              type="email" 
                              autoComplete="username" 
                              value={email} 
                              onChange={e => setEmail(e.target.value)} 
                              className="w-full bg-gray-900 text-white p-4 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 border border-gray-600 transition-all font-medium placeholder-gray-600" 
                              placeholder="admin@example.com" 
                              required
                          />
                      </div>
                      <div>
                          <label htmlFor="current-password" className="text-xs font-bold text-gray-500 uppercase ml-2 mb-1 block">Password</label>
                          <input 
                              id="current-password" 
                              name="password" 
                              type="password" 
                              autoComplete="current-password" 
                              value={password} 
                              onChange={e => setPassword(e.target.value)} 
                              className="w-full bg-gray-900 text-white p-4 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 border border-gray-600 transition-all font-medium placeholder-gray-600" 
                              placeholder="••••••••" 
                              required
                          />
                      </div>
                      
                      <div className="flex justify-end">
                          <button type="button" onClick={handleForgotPassword} className="text-xs text-indigo-400 hover:text-indigo-300 font-bold transition-colors">
                              Password dimenticata?
                          </button>
                      </div>

                      <button 
                          type="submit" 
                          disabled={isLoggingIn} 
                          className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl shadow-xl shadow-indigo-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
                      >
                          {isLoggingIn ? <Loader2 className="animate-spin"/> : <LogOut className="rotate-180" size={18}/>}
                          {isLoggingIn ? 'Verifica in corso...' : 'Accedi'}
                      </button>
                  </form>

                  {/* Back Button */}
                  <button onClick={handleSmartBackToHome} className="w-full py-3 text-gray-500 font-bold text-sm hover:text-white transition-colors">
                      Torna alla Home
                  </button>

                  <div className="absolute bottom-4 left-0 right-0 text-center text-[10px] text-gray-700 font-mono uppercase tracking-widest pointer-events-none">
                      v{APP_VERSION} • Secure Login
                  </div>
              </div>
          </div>
      );
  } else if (view === 'admin') {
      // PROTECTED ROUTE LOGIC
      if (!user && !initialLoading && !isLoggingIn) {
          // If NOT logged in, redirect to login
          setTimeout(() => setView('login'), 100); 
          content = <FullScreenLoader />;
      } else if (user && !activeTenantId && currentUserTenantId !== 'root') {
          // Fallback: Logged in but context lost (activeTenantId is missing)
          // Display explicit loading/error state instead of black screen
          content = (
              <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-white text-center p-6">
                  <Loader2 size={48} className="text-indigo-500 animate-spin mb-4" />
                  <h2 className="text-xl font-bold mb-2">Caricamento Profilo Admin...</h2>
                  <p className="text-gray-400 text-sm mb-6 max-w-md">Stiamo recuperando i dati del tuo negozio. Se persiste per più di 10 secondi, clicca su Ricarica.</p>
                  <div className="flex gap-3">
                      <button onClick={() => window.location.reload()} className="px-6 py-3 bg-indigo-600 rounded-xl font-bold hover:bg-indigo-500 transition-colors">Ricarica Pagina</button>
                      <button onClick={() => { if(window.confirm('Sei sicuro di voler uscire?')) { signOut(auth); setView('login'); } }} className="px-6 py-3 bg-gray-800 text-gray-400 rounded-xl font-bold hover:text-white transition-colors">Esci</button>
                  </div>
              </div>
          );
      } else {
          content = (
            <React.Suspense fallback={<FullScreenLoader />}>
            {/* FIXED ADMIN LAYOUT FOR FULL SCREEN SCROLLING */}
            {/* Use min-h-[100dvh] to respect iOS address bar dynamics */}
            <div 
                className="min-h-[100dvh] h-[100dvh] bg-gray-900 text-white flex flex-col overflow-hidden relative"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                 
                 {/* HEADER - Fixed at top with iOS Dynamic Island safe spacing */}
                 <div className="shrink-0 z-40 bg-gray-900/95 backdrop-blur-md border-b border-gray-800 px-3 sm:px-4 pb-3 safe-area-top-header flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
                     <div className="flex flex-col justify-center w-full sm:w-auto items-start">
                            <button onClick={() => { 
                                if (adminSubView === 'dashboard') {
                                    setIsAdminSidebarOpen(true);
                                } else {
                                    setAdminSubView('dashboard');
                                }
                            }} className="flex flex-col text-left hover:opacity-80 transition-opacity">
                                <h2 className="text-xl font-black flex items-center gap-2"><LayoutDashboard className="text-indigo-500"/> Admin Panel</h2>
                                <span className="text-[8px] text-gray-600 font-mono tracking-tighter uppercase">{APP_VERSION}</span>
                            </button>
                     </div>
                     <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto no-scrollbar pb-1 sm:pb-0 justify-start sm:justify-end [&>*]:shrink-0">
                        <IntercomMuteToggle t={t} />
                        <IntercomSystemTrigger t={t} />
                        <CameraMonitorTrigger t={t} />
                        <RiderMonitorTrigger t={t} /> 
                        <button 
                            onClick={() => setView('tv-menu')} 
                            className="flex flex-col lg:flex-row items-center justify-center gap-1 p-2 lg:px-3 lg:py-1.5 min-w-[50px] bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg lg:rounded-xl transition-colors border border-indigo-500 shadow-md" 
                            title="Modalità TV Menu"
                        >
                            <MonitorPlay size={18}/>
                            <span className="text-[9px] lg:text-xs font-bold uppercase">Menu TV</span>
                        </button>
                        <button 
                            onClick={() => setView('intro')} 
                            className="flex flex-col lg:flex-row items-center justify-center gap-1 p-2 lg:px-3 lg:py-1.5 min-w-[50px] bg-gray-800 hover:bg-gray-700 text-indigo-400 hover:text-white rounded-lg lg:rounded-xl transition-colors border border-gray-700" 
                            title={t('admin_header_app_title')}
                        >
                            <Eye size={18}/>
                            <span className="text-[9px] lg:text-xs font-bold uppercase">{t('admin_header_app')}</span>
                        </button>
                        <button onClick={() => setIsOrderListOpen(true)} className="flex flex-col lg:flex-row items-center justify-center gap-1 p-2 lg:px-3 lg:py-1.5 min-w-[50px] bg-gray-800 rounded-lg lg:rounded-xl hover:bg-gray-700 relative text-white" title={t('admin_header_orders_title')}>
                            <ClipboardList size={18}/>
                            <span className="text-[9px] lg:text-xs font-bold uppercase">{t('admin_header_orders')}</span>
                            {pendingOrdersCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 w-4 h-4 rounded-full text-[10px] flex items-center justify-center font-bold">{pendingOrdersCount}</span>}
                        </button>
                        <button onClick={() => { if(window.confirm('Sei sicuro di voler uscire?')) { signOut(auth); setView('intro'); } }} className="flex flex-col lg:flex-row items-center justify-center gap-1 p-2 lg:px-3 lg:py-1.5 min-w-[50px] bg-red-500/20 text-red-500 rounded-lg lg:rounded-xl hover:bg-red-500 hover:text-white" title={t('admin_header_exit_title')}>
                            <LogOut size={18}/>
                            <span className="text-[9px] lg:text-xs font-bold uppercase">{t('admin_header_exit')}</span>
                        </button>
                     </div>
                 </div>
                 
                 {/* SCROLLABLE CONTENT AREA */}
                 {/* Using flex-1 allows this area to take remaining space. overflow-y-auto enables scrolling ONLY here. */}
                 {/* z-50 ensures fixed modals inside this content are rendered above the z-40 header */}
                 <div className="flex-1 flex overflow-hidden relative z-50 w-full">
                     <React.Suspense fallback={<div className="w-64 bg-gray-900 border-r border-gray-800 hidden md:block"></div>}>
                         <AdminSidebar 
                             adminSubView={adminSubView} 
                             setAdminSubView={setAdminSubView} 
                             productsCount={products.length} 
                             ingredientsCount={ingredients.length} 
                             deactivatedProductsCount={products.filter(p => p.isActive === false).length} 
                             deactivatedIngredientsCount={ingredients.filter(i => i.isActive === false).length} 
                             isOpen={isAdminSidebarOpen}
                             setIsOpen={setIsAdminSidebarOpen}
                         />
                     </React.Suspense>
                     <div id="admin-scroll-container" ref={scrollContainerRef} className="flex-1 overflow-y-auto overscroll-contain p-4 md:p-6 pb-32 w-full relative" style={{ WebkitOverflowScrolling: 'touch' }}>
                         <div className="max-w-6xl mx-auto space-y-6 min-h-full flex flex-col">
                             {adminSubView === 'dashboard' && <AdminDashboard
                                   setAdminSubView={setAdminSubView}
                                   productsCount={products.length}
                                   ingredientsCount={ingredients.length}
                                   deactivatedProductsCount={products.filter(p => p.isActive === false).length}
                                   deactivatedIngredientsCount={ingredients.filter(i => i.isActive === false).length}
                             />}
                         {adminSubView === 'delivery' && <DeliveryManager settings={deliverySettings} onUpdate={() => {}} />}
                         {adminSubView === 'graphics' && <GraphicsManager settings={graphicsSettings} menus={menus} onUpdate={() => {}} />}
                         {adminSubView === 'opening-hours' && <OpeningHoursManager openingHours={settings.openingHours || []} onUpdate={() => {}} whatsappNumber={settings.whatsappNumber} takeAwayFee={settings.takeAwayFee} disposableFee={settings.disposableFee || 0} />}
                         {adminSubView === 'structure' && <StructureManager menus={menus} categories={categories} onUpdate={() => {}} />}
                         {adminSubView === 'products-list' && <ProductManager products={products} menus={menus} categories={categories} onUpdate={() => {}} />}
                         {adminSubView === 'ingredients-list' && <IngredientManager ingredients={ingredients} menus={menus} categories={categories} onUpdate={() => {}} />}
                         {adminSubView === 'customers-list' && <CustomerManager />}
                         {adminSubView === 'cameras' && <CameraManager />}
                         {adminSubView === 'orders-history' && <OrderHistoryManager />}
                         {adminSubView === 'analytics' && <AnalyticsManager />}
                         {adminSubView === 'fiscal-printer' && <FiscalPrinterManager />} 
                         {adminSubView === 'iot-printer' && <IotPrinterManager />} 
                         {adminSubView === 'rider-management' && <RiderManagement />} 
                         
                         {adminSubView === 'settings-general' && <GeneralSettingsSection />}
                         {adminSubView === 'settings-marketing' && <MarketingSection />}
                         {adminSubView === 'settings-system' && <SystemToolsSection />}
                     </div>
                 </div>
                 </div>
            </div>
            </React.Suspense>
          );
      }
  } else {
    content = <IntroView />;
  }

  return (
    <>
        <TenantMetaManager />
        <RecentOrderModal />
        <motion.div 
            key={activeTenantId + view + isKioskAttractActive}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="min-h-screen"
        >
            <React.Suspense fallback={<FullScreenLoader />}>
                {/* Kiosk Attract Screen */}
                {isKioskMode && view !== 'tv-menu' && view !== 'admin' && view !== 'super-admin' && view !== 'login' && view !== 'rider' && view !== 'rider-locked' && (
                    <div style={{ display: isKioskAttractActive ? 'block' : 'none' }}>
                        {settings.kioskAttractMode === 'TV_MENU' ? (
                            <React.Suspense fallback={<FullScreenLoader />}>
                                <TvMenuView />
                            </React.Suspense>
                        ) : (
                            <KioskAttractView />
                        )}
                    </div>
                )}
                
                {/* Main Ordering Flow */}
                <div style={{ display: (isKioskMode && isKioskAttractActive && view !== 'tv-menu' && view !== 'admin' && view !== 'super-admin' && view !== 'login' && view !== 'rider' && view !== 'rider-locked') ? 'none' : 'block' }}>
                    {content}
                </div>
            </React.Suspense>
        </motion.div>
        
        {/* MARKETING CARDS FOR CUSTOMERS */}
        {!shouldShowLanding && view !== 'admin' && view !== 'super-admin' && view !== 'rider-locked' && view !== 'rider' && view !== 'login' && view !== 'cart' && !isKioskAttractActive && (
            <MarketingFloatingWidget />
        )}

        <ToastNotification />
        <OrderStatusNotification />
        <React.Suspense fallback={null}>
            {user && view !== 'rider-locked' && view !== 'login' && view !== 'rider' && <LiveOrderList />}
            <IntercomSystem />
            <RiderTracker />
            {user && <CameraMonitor />}
            {user && <RiderMonitor />}
            {isSuperAdminOpen && <SuperAdminDashboard onClose={() => setIsSuperAdminOpen(false)} />}
            
            {/* SUPER ADMIN RETURN BUTTON */}
            {isSuperUser && activeTenantId && activeTenantId !== 'root' && view !== 'rider-locked' && (
                <motion.div
                    drag
                    dragMomentum={false}
                    className="fixed top-24 left-4 z-[9999] cursor-move select-none"
                >
                    <button
                        onClick={handleReturnToSuperAdmin}
                        className="bg-red-600 text-white px-4 py-2 rounded-full shadow-2xl font-bold text-xs flex items-center gap-2 border-2 border-white animate-in slide-in-from-top-4 hover:bg-red-700 transition-colors pointer-events-auto"
                    >
                        <ShieldAlert size={16} /> SUPER ADMIN: TORNA ALLA BASE
                    </button>
                </motion.div>
            )}

            {/* RIDER IMPERSONATION OVERLAY FOR SUPER ADMIN */}
            {isSuperUser && view === 'rider' && (
                <motion.div
                    drag
                    dragMomentum={false}
                    style={{ touchAction: 'none' }}
                    className="fixed top-4 left-4 md:top-6 md:left-6 z-[9999] cursor-move select-none"
                >
                    <div className="bg-amber-600 border-2 border-amber-400 text-white px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl shadow-2xl font-bold text-xs sm:text-sm flex items-center gap-3 sm:gap-4 animate-in slide-in-from-top-4">
                        <div className="flex items-center gap-2">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
                            </span>
                            <span>IMPERSONIFICAZIONE RIDER (Spostabile)</span>
                        </div>
                        <button
                            onClick={handleStopRiderImpersonation}
                            className="bg-white text-amber-950 px-3 py-1.5 rounded-xl text-[10px] sm:text-xs font-black uppercase hover:bg-amber-100 transition-all flex items-center gap-1 active:scale-95 pointer-events-auto shrink-0"
                        >
                            <ShieldAlert size={14} /> TORNA AL SUPER ADMIN
                        </button>
                    </div>
                </motion.div>
            )}
        </React.Suspense>
    </>
  );
};

const IntercomSystemTrigger = ({ t }: { t: (key: string) => string }) => {
    const handleStart = () => { window.dispatchEvent(new Event('start-intercom-call')); };
    return (
        <button onClick={handleStart} className="flex flex-col lg:flex-row items-center justify-center gap-1 p-2 lg:px-3 lg:py-1.5 min-w-[50px] bg-red-600 rounded-lg lg:rounded-xl hover:bg-red-700 text-white transition-colors" title={t('admin_header_intercom_title')}>
            <Video size={18}/>
            <span className="text-[9px] lg:text-xs font-bold uppercase">{t('admin_header_intercom')}</span>
        </button>
    );
};

const IntercomMuteToggle = ({ t }: { t: (key: string) => string }) => {
    const [isMuted, setIsMuted] = useState(() => localStorage.getItem('intercom_device_muted') === 'true');
    const toggleMute = () => {
        const newMuted = !isMuted;
        setIsMuted(newMuted);
        localStorage.setItem('intercom_device_muted', String(newMuted));
        window.dispatchEvent(new CustomEvent('intercom-mute-changed', { detail: newMuted }));
    };
    return (
        <button onClick={toggleMute} className={`flex flex-col lg:flex-row items-center justify-center gap-1 p-2 lg:px-3 lg:py-1.5 min-w-[50px] rounded-lg lg:rounded-xl transition-colors ${isMuted ? 'bg-red-500/20 text-red-500 hover:bg-red-500 hover:text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'}`} title={isMuted ? t('admin_header_unmute_title') : t('admin_header_mute_title')}>
            {isMuted ? <BellOff size={18}/> : <Bell size={18}/>}
            <span className="text-[9px] lg:text-xs font-bold uppercase">{isMuted ? t('admin_header_mute') : t('admin_header_notif')}</span>
        </button>
    );
};

const CameraMonitorTrigger = ({ t }: { t: (key: string) => string }) => {
    const handleOpen = () => { window.dispatchEvent(new Event('open-cctv')); };
    return (
        <button onClick={handleOpen} className="flex flex-col lg:flex-row items-center justify-center gap-1 p-2 lg:px-3 lg:py-1.5 min-w-[50px] bg-gray-800 rounded-lg lg:rounded-xl hover:bg-gray-700 text-white transition-colors" title={t('admin_header_cam_title')}>
            <Camera size={18}/>
            <span className="text-[9px] lg:text-xs font-bold uppercase">{t('admin_header_cam')}</span>
        </button>
    );
};

const RiderMonitorTrigger = ({ t }: { t: (key: string) => string }) => {
    const handleOpen = () => { window.dispatchEvent(new Event('open-rider-map')); };
    return (
        <button onClick={handleOpen} className="flex flex-col lg:flex-row items-center justify-center gap-1 p-2 lg:px-3 lg:py-1.5 min-w-[50px] bg-amber-600 rounded-lg lg:rounded-xl hover:bg-amber-700 text-white transition-colors" title={t('admin_header_rider_title')}>
            <Bike size={18}/>
            <span className="text-[9px] lg:text-xs font-bold uppercase">{t('admin_header_rider')}</span>
        </button>
    );
};

// --- MAIN APP COMPONENT ---
// Ensures ErrorBoundary is at the top level and AppProvider wraps consumers.
export default function App() { 
    return (
        <ErrorBoundary>
            <AppProvider>
                <AppContent />
                <PwaInstallPrompt />
            </AppProvider>
        </ErrorBoundary>
    ); 
}
